/**
 * Map interactions - click, hover, tooltip handling.
 * Wires map events to sidebar and search.
 *
 * Tooltip priority:
 *   1. Overlay feature under cursor (airport, fan, airspace, hazard, park) → feature tooltip
 *   2. Country under cursor → country tooltip
 *   3. Open ocean → no tooltip
 */
import type mapboxgl from 'mapbox-gl';
import type { CountryData } from '../types';
import { STATUS_CONFIG } from '../types';
import { setHoveredCountry } from './layers';
import { flyToCountry } from './globe';
import { setCountryURL } from '../url';

/** Country centroid lookup (iso2 -> [lng, lat]) */
import { COUNTRY_CENTROIDS } from '../data/centroids';
import { isMeasureActive } from './measure';
import { getUnits, formatElevation, formatAltitude, onUnitsChange } from '../units';

type CountrySelectCallback = (country: CountryData) => void;

// ── All overlay layer IDs, grouped by priority (first match wins) ──────────

/** Point layers checked first (highest priority) */
const POINT_LAYERS = [
  'drone-projects-dots',
  'airport-large', 'airport-medium', 'airport-small', 'airport-heliport',
  'hazard-mil-points', 'hazard-prison-points', 'hazard-gov-points',
];

/** Polygon / circle layers checked second */
const AREA_LAYERS = [
  // Runway fans & outlines
  'runway-no-fly', 'runway-alt-60', 'runway-alt-150', 'runway-outline',
  // Awareness circles
  'awareness-10nm', 'awareness-5nm', 'awareness-3nm', 'awareness-1nm',
  // Airspace
  'airspace-zones',
  // Hazard polygons
  'hazard-mil-fill', 'hazard-prison-fill',
  // Parks
  'drone-parks-fill',
];

const ALL_OVERLAY_LAYERS = [...POINT_LAYERS, ...AREA_LAYERS];

// ── Tooltip formatters per layer ──────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  large_airport:  'Large Airport',
  medium_airport: 'Medium Airport',
  small_airport:  'Small Airport',
  heliport:       'Heliport',
};

const AWARENESS_LABELS: Record<string, { label: string; color: string }> = {
  'awareness-10nm': { label: '10 NM Major Airport Zone', color: '#dc2626' },
  'awareness-5nm':  { label: '5 NM Control Zone',        color: '#3b82f6' },
  'awareness-3nm':  { label: '3 NM Airport Boundary',    color: '#60a5fa' },
  'awareness-1nm':  { label: '1 NM Heliport Zone',       color: '#22d3ee' },
};

/** Fan labels — altitude values are dynamic based on unit system */
function getFanLabel(layerId: string): { label: string; color: string } | null {
  switch (layerId) {
    case 'runway-no-fly':  return { label: 'No Fly Zone',                     color: '#ef4444' };
    case 'runway-alt-60':  return { label: `Max ${formatAltitude(60)}`,        color: '#fb923c' };
    case 'runway-alt-150': return { label: `Max ${formatAltitude(150)}`,       color: '#facc15' };
    case 'runway-outline': return { label: 'Runway',                           color: '#ef4444' };
    default: return null;
  }
}

const AIRSPACE_META: Record<string, { label: string; color: string }> = {
  PROHIBITED: { label: 'Prohibited Airspace',    color: '#b71c1c' },
  RESTRICTED: { label: 'Restricted Airspace',    color: '#e53935' },
  CTR:        { label: 'Control Zone (CTR)',      color: '#e53935' },
  TMA:        { label: 'Terminal Area (TMA)',     color: '#e57373' },
  DANGER:     { label: 'Danger Zone',             color: '#fb8c00' },
  TRA:        { label: 'Temporary Reserved Area', color: '#f57c00' },
  ADIZ:       { label: 'Air Defense ID Zone',     color: '#7b1fa2' },
  RMZ:        { label: 'Radio Mandatory Zone',    color: '#2e7d32' },
  OTHER:      { label: 'Airspace',                color: '#78788c' },
};

const HAZARD_META: Record<string, { label: string; color: string; icon: string }> = {
  military:   { label: 'Military Zone',               color: '#ef4444', icon: '⚠️' },
  prison:     { label: 'Prison / Correctional',       color: '#a855f7', icon: '🔒' },
  government: { label: 'Government Building',         color: '#6366f1', icon: '🏛️' },
};

/**
 * Build overlay tooltip HTML from a feature + the layer it came from.
 * Returns null if no meaningful tooltip can be built.
 */
function overlayTooltipHTML(feature: mapboxgl.GeoJSONFeature, layerId: string): string | null {
  const p = feature.properties as Record<string, string>;

  // ── Airport point ───────────────────────────────────────
  if (layerId.startsWith('airport-')) {
    const typeLabel = TYPE_LABELS[p.type] ?? p.type;
    return `
      <div class="tooltip-overlay">
        <div class="tooltip-overlay-title">${p.name || p.icao}</div>
        <div class="tooltip-overlay-sub">${p.icao}${p.iata ? ' / ' + p.iata : ''}</div>
        <div class="tooltip-overlay-meta">${typeLabel}</div>
      </div>`;
  }

  // ── Awareness circles ───────────────────────────────────
  if (layerId.startsWith('awareness-')) {
    const info = AWARENESS_LABELS[layerId];
    if (!info) return null;
    return `
      <div class="tooltip-overlay">
        <div class="tooltip-overlay-title" style="color:${info.color}">${info.label}</div>
        <div class="tooltip-overlay-sub">${p.name || p.icao}</div>
        <div class="tooltip-overlay-meta">${TYPE_LABELS[p.type] ?? p.type}</div>
      </div>`;
  }

  // ── Runway fans & outline ───────────────────────────────
  if (layerId.startsWith('runway-')) {
    const info = getFanLabel(layerId);
    if (!info) return null;
    return `
      <div class="tooltip-overlay">
        <div class="tooltip-overlay-title" style="color:${info.color}">${info.label}</div>
        <div class="tooltip-overlay-sub">${p.airport_name || p.icao}</div>
        ${p.runway ? `<div class="tooltip-overlay-meta">${p.runway}</div>` : ''}
      </div>`;
  }

  // ── Airspace zone ───────────────────────────────────────
  if (layerId === 'airspace-zones') {
    const meta = AIRSPACE_META[p.type] ?? AIRSPACE_META.OTHER;
    return `
      <div class="tooltip-overlay">
        <div class="tooltip-overlay-title" style="color:${meta.color}">${meta.label}</div>
        <div class="tooltip-overlay-sub">${p.name || ''}</div>
      </div>`;
  }

  // ── Hazards (military / prison / government) ────────────
  if (layerId.startsWith('hazard-')) {
    const meta = HAZARD_META[p.category] ?? { label: p.category, color: '#94a3b8', icon: '📍' };
    return `
      <div class="tooltip-overlay">
        <div class="tooltip-overlay-title" style="color:${meta.color}">${meta.icon} ${meta.label}</div>
        ${p.name ? `<div class="tooltip-overlay-sub">${p.name}</div>` : ''}
      </div>`;
  }

  // ── Parks ───────────────────────────────────────────────
  if (layerId === 'drone-parks-fill') {
    return `
      <div class="tooltip-overlay">
        <div class="tooltip-overlay-title" style="color:#f9a825">🌿 Protected Area</div>
        ${p.name ? `<div class="tooltip-overlay-sub">${p.name}</div>` : ''}
      </div>`;
  }

  return null;
}

// ── Setup ─────────────────────────────────────────────────────────────────

export function setupInteractions(
  map: mapboxgl.Map,
  countries: CountryData[],
  onCountrySelect: CountrySelectCallback
) {
  const countryMap = new Map<string, CountryData>();
  for (const c of countries) {
    countryMap.set(c.iso2, c);
  }

  const tooltip = document.getElementById('tooltip')!;

  // ── Hover: overlay features take priority over country tooltip ──────────
  map.on('mousemove', 'country-hit-area', (e) => {
    if (!e.features?.length) return;

    // Check overlay layers for a feature under the cursor
    const activeLayers = ALL_OVERLAY_LAYERS.filter(l => !!map.getLayer(l));
    let overlayHit: { feature: mapboxgl.GeoJSONFeature; layerId: string } | null = null;

    if (activeLayers.length > 0) {
      const hits = map.queryRenderedFeatures(e.point, { layers: activeLayers });
      if (hits.length > 0) {
        // Find which layer this feature belongs to (use layer.id from the feature)
        overlayHit = { feature: hits[0], layerId: (hits[0].layer as any).id };
      }
    }

    if (overlayHit) {
      // Overlay tooltip — suppress country highlight
      map.getCanvas().style.cursor = 'pointer';
      setHoveredCountry(map, null);

      const html = overlayTooltipHTML(overlayHit.feature, overlayHit.layerId);
      if (html) {
        tooltip.innerHTML = html;
        tooltip.classList.add('visible');
        tooltip.style.left = `${e.point.x + 16}px`;
        tooltip.style.top = `${e.point.y - 10}px`;
      }
    } else {
      // Bare country — highlight its border on hover
      map.getCanvas().style.cursor = 'pointer';
      const iso2 = e.features[0].properties?.iso_3166_1 as string;
      setHoveredCountry(map, iso2 || null);
      tooltip.classList.remove('visible');
    }
  });

  map.on('mouseleave', 'country-hit-area', () => {
    map.getCanvas().style.cursor = '';
    setHoveredCountry(map, null);
    tooltip.classList.remove('visible');
  });

  // ── Click: fly to country + open sidebar ────────────────────────────────
  // Skip if click landed on an overlay feature or if measuring.
  map.on('click', 'country-hit-area', (e) => {
    if (!e.features?.length) return;
    if (isMeasureActive()) return; // measuring mode absorbs clicks
    const overlayLayers = ALL_OVERLAY_LAYERS.filter(l => !!map.getLayer(l));
    if (overlayLayers.length > 0 && map.queryRenderedFeatures(e.point, { layers: overlayLayers }).length > 0) return;

    const iso2 = e.features[0].properties?.iso_3166_1 as string;
    const country = countryMap.get(iso2);

    if (country) {
      const centroid = COUNTRY_CENTROIDS[country.iso2];
      if (centroid) {
        flyToCountry(map, centroid[0], centroid[1]);
      }
      onCountrySelect(country);
      setCountryURL(country.iso2);
      tooltip.classList.remove('visible');
    }
  });

  // ── Elevation + coordinates bar (global mousemove, rAF-throttled) ───────
  const elevBar = document.getElementById('elevation-bar');
  if (elevBar) {
    let rafPending = false;
    let lastLngLat: { lng: number; lat: number } | null = null;
    let lastElev: number | null = null;

    const renderElevBar = () => {
      if (!lastLngLat) return;
      const { lng, lat } = lastLngLat;
      const latDir = lat >= 0 ? 'N' : 'S';
      const lngDir = lng >= 0 ? 'E' : 'W';
      const coordStr = `${Math.abs(lat).toFixed(1)}°${latDir}, ${Math.abs(lng).toFixed(1)}°${lngDir}`;
      const elevStr = (lastElev !== null && lastElev !== undefined)
        ? formatElevation(lastElev)
        : '—';
      elevBar.textContent = `${coordStr} · ${elevStr}`;
    };

    map.on('mousemove', (e) => {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(() => {
        rafPending = false;
        lastLngLat = { lng: e.lngLat.lng, lat: e.lngLat.lat };
        lastElev = map.queryTerrainElevation(e.lngLat) ?? null;
        renderElevBar();
      });
    });

    // Re-render when units change (so displayed value updates immediately)
    onUnitsChange(() => renderElevBar());

    // Hide when cursor leaves the map
    map.getCanvas().addEventListener('mouseleave', () => {
      elevBar.textContent = '';
      lastLngLat = null;
    });
  }
}
